import React, { useState, useEffect, useMemo } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { 
  FaEye, FaTrashAlt, FaSearch, FaTimes, FaUser, 
  FaShippingFast, FaCreditCard, FaBoxOpen, FaCalendarAlt, 
  FaTruckLoading, FaPhone, FaEnvelope, FaMapMarkerAlt, FaRupeeSign 
} from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import { fetchUnverifiedOrders, fetchVerifiedOrders } from '../api/api';

const OrderInProcess: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAgent, setIsAgent] = useState<boolean>(false);
  
  const [searchIdOrName, setSearchIdOrName] = useState<string>(''); 
  const [filterPayment, setFilterPayment] = useState<string>('All'); 
  const [filterDate, setFilterDate] = useState<string>(''); 

  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);

  useEffect(() => {
    const checkUserRole = () => {
        try {
            const token = localStorage.getItem('token');
            if (token) {
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const payload = JSON.parse(decodeURIComponent(window.atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')));
                if (JSON.stringify(payload).toUpperCase().includes('AGENT')) setIsAgent(true);
            }
        } catch (e) { console.error("Error decoding token", e); }
    };
    checkUserRole();
  }, []);

  const fetchInProcessOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      // Fetch CRM Orders
      const [unverifiedRes, verifiedRes] = await Promise.all([
          fetchUnverifiedOrders().catch(() => ({ data: [] })),
          fetchVerifiedOrders().catch(() => ({ data: [] }))
      ]);

      const crmOrders = [...(unverifiedRes.data || []), ...(verifiedRes.data || [])].map((o: any) => ({
          id: o.id,
          user: {
              firstName: o.mainUser?.firstName || o.customerName || 'CRM Customer',
              lastName: o.mainUser?.lastName || '',
              email: o.mainUser?.email || o.email || 'N/A',
              mobile: o.mainUser?.mobile || o.mobile || 'N/A'
          },
          orderDate: o.createdAt || new Date().toISOString(),
          totalAmount: o.payableAmount || 0,
          status: o.status || (o.waybill ? 'SHIPPED' : 'UNVERIFIED'),
          paymentMode: 'COD',
          paymentStatus: 'PENDING',
          source: 'CRM',
          originalData: o
      }));

      let unifiedOrders = [];

      if (isAgent) {
         unifiedOrders = crmOrders;
      } else {
         const webRes = await axios.get('https://www.ayushvatika.com/api/orders/all', {
            headers: { 'Authorization': `Bearer ${token}` }
         }).catch(() => ({ data: [] }));

         const webOrders = (webRes.data || []).map((o: any) => ({ ...o, source: 'WEBSITE', originalData: o }));
         unifiedOrders = [...webOrders, ...crmOrders];
      }

      // Filter logic: Only Shipped or Out For Delivery
      const processingOrders = unifiedOrders.filter((order: any) => 
        order.status === 'SHIPPED' || order.status === 'OUT_FOR_DELIVERY' || order.status === 'IN_TRANSIT'
      );
      
      processingOrders.sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());
      setOrders(processingOrders);

    } catch (err: any) {
      toast.error("Orders load nahi ho paye!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInProcessOrders(); }, [isAgent]);

  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const customerFullName = `${order.user.firstName} ${order.user.lastName}`.toLowerCase();
      const matchesSearch = order.id.toLowerCase().includes(searchIdOrName.toLowerCase()) || customerFullName.includes(searchIdOrName.toLowerCase());
      const matchesPayment = filterPayment === 'All' || order.paymentMode === filterPayment || order.paymentStatus === filterPayment;
      const orderDateOnly = new Date(order.orderDate).toISOString().split('T')[0];
      const matchesDate = !filterDate || orderDateOnly === filterDate;
      return matchesSearch && matchesPayment && matchesDate;
    });
  }, [orders, searchIdOrName, filterPayment, filterDate]);

  const handleStatusChange = async (id: string, newStatus: string, source: string) => {
    if(source === 'CRM') return; // Delhivery handles CRM status updates
    try {
      const token = localStorage.getItem("token");
      let shippingStatus = newStatus === 'DELIVERED' ? 'DELIVERED' : 'IN_TRANSIT';
      if(newStatus === 'OUT_FOR_DELIVERY') shippingStatus = 'OUT_FOR_DELIVERY';

      await axios.put(`https://www.ayushvatika.com/api/orders/${id}/status?orderStatus=${newStatus}&shippingStatus=${shippingStatus}`, {}, { headers: { 'Authorization': `Bearer ${token}` } });
      toast.success(`Order status updated to ${newStatus}`);
      fetchInProcessOrders(); 
    } catch (err: any) { toast.error("Status update fail ho gaya!"); }
  };

  const handleDelete = async (id: string, source: string) => {
    if (!window.confirm("Delete this in-process order record?")) return;
    try {
      const token = localStorage.getItem("token");
      if(source === 'CRM') {
          await axios.delete(`https://www.ayushvatika.com/api/admin/delete-order/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      } else {
          await axios.delete(`https://www.ayushvatika.com/api/orders/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      }
      toast.success("Order deleted!");
      fetchInProcessOrders();
    } catch (err) { toast.error("Delete failed!"); }
  };

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors">
        <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-xl shadow-sm">
                <FaTruckLoading size={24}/>
            </div>
            <h1 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight">Orders In Process</h1>
        </div>
        
        {/* --- Search & Filters --- */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8 bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
             <div className="relative">
                <input type="text" placeholder="Search ID/Name..." className="w-full p-3 pl-10 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white" value={searchIdOrName} onChange={(e) => setSearchIdOrName(e.target.value)} />
                <FaSearch className="absolute left-3.5 top-4 text-gray-400" />
            </div>
            <select className="p-3 border border-gray-200 dark:border-gray-600 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" value={filterPayment} onChange={(e) => setFilterPayment(e.target.value)}>
                <option value="All">All Payments</option>
                <option value="COD">COD</option>
                <option value="PAID">PAID</option>
            </select>
            <div className="relative">
                <input type="date" className="w-full p-3 pl-10 border border-gray-200 dark:border-gray-600 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} />
                <FaCalendarAlt className="absolute left-3.5 top-4 text-gray-400" />
            </div>
        </div>

        {/* --- Responsive Table --- */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700 uppercase text-[10px] font-black text-gray-400 dark:text-gray-300 tracking-widest">
                <tr>
                  <th className="px-6 py-5 text-left">Order ID</th>
                  <th className="px-6 py-5 text-left">Customer</th>
                  <th className="px-6 py-5 text-left hidden sm:table-cell">Shipping Date</th>
                  <th className="px-6 py-5 text-left">Amount</th>
                  <th className="px-6 py-5 text-left">Live Status</th>
                  <th className="px-6 py-5 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                {loading ? (
                  <tr><td colSpan={6} className="text-center py-20 italic text-gray-400">Loading processing orders...</td></tr>
                ) : filteredOrders.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-20 text-gray-400 font-bold">No orders currently in process.</td></tr>
                ) : filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-blue-50/10 dark:hover:bg-blue-900/10 transition-all">
                    <td className="px-6 py-5 text-xs font-mono font-black text-blue-800 dark:text-blue-400 whitespace-nowrap">
                        {String(order.id).substring(0, 8)} <br/>
                        <span className="text-[9px] text-gray-400">{order.source}</span>
                    </td>
                    <td className="px-6 py-5">
                      <div className="text-sm font-black text-gray-800 dark:text-white">{order.user.firstName}</div>
                      <div className="text-[10px] text-gray-400 hidden sm:block">{order.user.email}</div>
                    </td>
                    <td className="px-6 py-5 text-xs text-gray-600 dark:text-gray-400 font-bold hidden sm:table-cell">
                      {new Date(order.orderDate).toLocaleDateString('en-GB')}
                    </td>
                    <td className="px-6 py-5 text-sm font-black text-gray-900 dark:text-white whitespace-nowrap">₹{order.totalAmount}</td>
                    <td className="px-6 py-5">
                        {order.source === 'CRM' ? (
                            <span className="px-3 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded-lg text-[10px] font-black border border-purple-200 dark:border-purple-800 tracking-wider">TRACK VIA AWB</span>
                        ) : (
                            <select 
                              value={order.status} 
                              onChange={(e) => handleStatusChange(order.id, e.target.value, order.source)}
                              className={`text-[10px] font-black p-2 border rounded-lg outline-none cursor-pointer ${
                                 order.status === 'OUT_FOR_DELIVERY' 
                                 ? 'border-orange-200 bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300' 
                                 : 'border-blue-200 bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                              }`}
                            >
                              <option value="SHIPPED">SHIPPED</option>
                              <option value="OUT_FOR_DELIVERY">OUT FOR DELIVERY</option>
                              <option value="DELIVERED">DELIVER NOW</option>
                              <option value="CANCELLED">CANCEL ORDER</option>
                            </select>
                        )}
                    </td>
                    <td className="px-6 py-5 text-center">
                        <div className="flex justify-center gap-3">
                            <button onClick={() => { setSelectedOrder(order); setShowModal(true); }} className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-all shadow-sm"><FaEye size={18}/></button>
                            {!isAgent && (
                                <button onClick={() => handleDelete(order.id, order.source)} className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all shadow-sm"><FaTrashAlt size={18}/></button>
                            )}
                        </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* --- MODAL (Kept exact same structure as PlacedOrderList above for uniformity) --- */}
      {showModal && selectedOrder && (
         /* Modal Content Same As PlacedOrderList.tsx - Sirf title me 'In Process Details' */
         <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 backdrop-blur-sm animate-fade-in">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-blue-600 p-5 flex justify-between items-center text-white">
                <div className="flex items-center gap-3">
                    <div className="bg-white/20 p-2 rounded-lg"><FaTruckLoading size={24} /></div>
                    <div>
                        <h2 className="text-xl font-bold">Shipping Details <span className="text-xs bg-white/20 px-2 py-0.5 rounded ml-2">{selectedOrder.source}</span></h2>
                        <p className="text-xs text-blue-100 opacity-90 font-mono">ID: {selectedOrder.id}</p>
                    </div>
                </div>
                <button onClick={() => setShowModal(false)} className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors"><FaTimes size={20}/></button>
            </div>

            <div className="p-6 overflow-y-auto custom-scrollbar space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800 gap-4">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Current Status:</span>
                        <span className="px-3 py-1 rounded-full text-xs font-bold border bg-blue-100 text-blue-700 border-blue-200">{selectedOrder.status.replace(/_/g, " ")}</span>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
                        <FaCalendarAlt className="text-blue-500"/> 
                        Order Date: <span className="font-semibold text-gray-900 dark:text-white">{new Date(selectedOrder.orderDate).toLocaleDateString('en-GB')}</span>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-6">
                        <div className="bg-gray-50 dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700">
                             <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2"><FaUser className="text-blue-500"/> Customer Info</h4>
                             <div className="space-y-3">
                                <div className="flex justify-between"><span className="text-sm text-gray-500 dark:text-gray-400">Name</span><span className="text-sm font-semibold text-gray-900 dark:text-white">{selectedOrder.user.firstName} {selectedOrder.user.lastName}</span></div>
                                <div className="flex justify-between"><span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1"><FaEnvelope size={10}/> Email</span><span className="text-sm font-medium text-gray-700 dark:text-gray-300">{selectedOrder.user.email}</span></div>
                                <div className="flex justify-between"><span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1"><FaPhone size={10}/> Mobile</span><span className="text-sm font-medium text-gray-700 dark:text-gray-300">{selectedOrder.user.mobile || 'N/A'}</span></div>
                             </div>
                        </div>

                        <div className="bg-gray-50 dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700">
                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2"><FaMapMarkerAlt className="text-red-500"/> Delivery Location</h4>
                             <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed font-medium">
                                {selectedOrder.source === 'WEBSITE' ? selectedOrder.originalData.shippingAddressSnapshot : `${selectedOrder.originalData.houseNumber || ''}, ${selectedOrder.originalData.streetArea || ''}, ${selectedOrder.originalData.city || ''}, ${selectedOrder.originalData.state || ''} - ${selectedOrder.originalData.pincode || ''}`}
                             </p>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700">
                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2"><FaCreditCard className="text-emerald-500"/> Payment Info</h4>
                             <div className="flex justify-between items-center mb-2"><span className="text-sm text-gray-500">Mode</span><span className="font-bold text-gray-800 dark:text-white">{selectedOrder.paymentMode || 'COD'}</span></div>
                             <div className="flex justify-between items-center pt-3 border-t border-dashed border-gray-200 dark:border-gray-600"><span className="text-sm text-gray-500">Total Amount</span><span className="text-2xl font-bold text-blue-600 dark:text-blue-400 flex items-center"><FaRupeeSign size={18}/> {selectedOrder.totalAmount}</span></div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl overflow-hidden">
                            <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 border-b border-gray-100 dark:border-gray-600">
                                <h4 className="text-xs font-bold text-gray-500 dark:text-gray-300 flex items-center gap-2"><FaBoxOpen className="text-purple-500"/> Order Contents</h4>
                            </div>
                            <div className="max-h-48 overflow-y-auto custom-scrollbar p-2">
                                {selectedOrder.source === 'WEBSITE' ? selectedOrder.originalData.orderItems?.map((item: any, idx: number) => (
                                    <div key={idx} className="flex items-center gap-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors">
                                        <img src={item.imageUrl || '/Logo/default-product.png'} className="w-12 h-12 rounded-lg object-cover border border-gray-200 dark:border-gray-600" alt="prod" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-gray-800 dark:text-white truncate">{item.productName}</p>
                                            <p className="text-xs text-gray-500">Qty: {item.quantity} × ₹{item.price}</p>
                                        </div>
                                        <div className="text-sm font-bold text-gray-900 dark:text-gray-100">₹{item.quantity * item.price}</div>
                                    </div>
                                )) : (
                                    <div className="flex items-center gap-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors">
                                        <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-600 flex justify-center items-center"><FaBoxOpen className="text-gray-400"/></div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-gray-800 dark:text-white truncate">{selectedOrder.originalData.medicine}</p>
                                            <p className="text-xs text-gray-500">Qty: {selectedOrder.originalData.medicineQuantity} × ₹{selectedOrder.originalData.medicinePrice}</p>
                                        </div>
                                        <div className="text-sm font-bold text-gray-900 dark:text-gray-100">₹{selectedOrder.originalData.payableAmount}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="p-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                <button onClick={() => setShowModal(false)} className="px-6 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white rounded-lg text-sm font-bold transition-all">Close Details</button>
            </div>
          </div>
        </div>
      )}
      <style>{` .custom-scrollbar::-webkit-scrollbar { width: 6px; } .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; } .dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #4b5563; } `}</style>
    </AdminLayout>
  );
};

export default OrderInProcess;